import getBlogArticleCards from 'api/getBlogArticleCards';
import { fromEvent } from 'rxjs';

const subscriptions = [];
const PAGE_SIZE = 8;

/**
 * Extracts article cards from the provided HTML string.
 * @param htmlText
 * @returns {HTMLCollection|null}
 */
function processCards(htmlText) {
  // creating a fragment in order to parse the returned HTML string
  // and getting the new cards from it
  const fragment = document.createRange().createContextualFragment(htmlText);
  const newCardsContainer = fragment.querySelector('[data-item-list]');

  if (!newCardsContainer || !newCardsContainer.children) {
    // if we don't have a container or the container is empty, we cannot proceed
    throw new Error('Card list empty or not found.');
  }

  return newCardsContainer.children;
}

/**
 * Extracts default request parameters from a section element.
 * @param section
 * @returns {any|null}
 */
function getDefaultRequestsParams (section) {
  const requestParamsContainer = section.querySelector('[data-request-more-parameters]');
  try {
    return JSON.parse(requestParamsContainer.textContent);
  } catch (e) {
    return null;
  }
}

/**
 * Gets the current count of article cards in the container.
 * @param articlesContainer
 * @returns {number}
 */
function getCurrentCardsCount(articlesContainer) {
  return articlesContainer.querySelectorAll('[data-article-card]').length;
}

/**
 * Calculates the current page based on the number of cards already loaded.
 * @param currentCardsCount
 * @returns {number}
 */
function getCurrentPage(currentCardsCount) {
  return Math.floor(currentCardsCount / PAGE_SIZE);
}

/**
 * Fetches more articles based on the current state and default parameters.
 * @param {number} currentCardsCount
 * @param {{}} defaultParams
 * @returns {Promise<never>|Promise<string|null>|*}
 */
function getMoreArticles(currentCardsCount, defaultParams) {
  if (currentCardsCount >= defaultParams.TotalItems) {
    // if we already have all the cards, we do nothing
    return Promise.reject(new Error('No more articles found.'));
  }

  // if the page is 0, meaning we have less than a page worth of cards,
  // so we'll fetch the first page and "merge" the incoming cards with the current ones
  // and have a full page worth of cards
  const currentPage = getCurrentPage(currentCardsCount);
  return getBlogArticleCards({
    cardType: defaultParams.ArticleCardType,
    categories: defaultParams.CategoryIds,
    client: defaultParams.ContentClient,
    page: currentPage + 1,
    size: PAGE_SIZE,
  });
}

/**
 * Updates the article cards in the container with new cards.
 * @param section
 * @param articlesContainer
 * @param cards
 */
function updateCardList(articlesContainer, cards) {
  // we replace the initial 4 cards with the first page (that should contain 8 cards
  // including the ones we already have), then we just append the other cards
  const currentCardsCount = getCurrentCardsCount(articlesContainer);
  const currentPage = getCurrentPage(currentCardsCount);
  if (currentPage === 0) {
    articlesContainer.replaceChildren(...cards);
  } else {
    articlesContainer.append(...cards);
  }
}

/**
 * Enables the "Load More" button if there are more articles to load.
 * @param articlesContainer
 * @param defaultParams
 * @param button
 */
function enableButton(articlesContainer, defaultParams, button) {
  // Checking if there are still more cards available to load
  // and the button should be enabled again
  const newTotal = getCurrentCardsCount(articlesContainer);
  if (newTotal < defaultParams.TotalItems) {
    button.disabled = false;
  }
}

/**
 * Handles the "Load More" button click event to fetch and display more articles.
 * @param {Event} event
 * @returns {void}
 */
function loadMoreHandler(event) {
  const button = event.target;
  // we disable the button to prevent multiple clicks while processing
  button.disabled = true;
  const section = button.closest('[data-listing-section]');
  const articlesContainer = section.querySelector('[data-item-list]');
  const defaultParams = getDefaultRequestsParams(section);
  const initialCardCount = getCurrentCardsCount(articlesContainer);

  if (!defaultParams) {
    // if there is no request params, we cannot proceed
    return;
  }

  getMoreArticles(initialCardCount, defaultParams)
    .then(processCards)
    .then((cards) => updateCardList(articlesContainer, cards))
    .then(() => enableButton(articlesContainer, defaultParams, button))
    .catch((error) => console.error(error));
}

/**
 * Initializes the "Load More" button functionality by attaching click event listeners.
 */
export default function init() {
  const buttons = document.querySelectorAll('[data-load-more-button]');

  buttons.forEach(button => {
    subscriptions.push(fromEvent(button, 'click').subscribe(loadMoreHandler));
  });
}
